import { cn } from "@/components/podkit/lib/cn";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { TEMPLATE_GROUPS, type AutomationTemplate } from "@/utils/automation-templates";
import IconChevronRight from "central-icons/IconChevronRight";
import workflowDashboardImage from "@/assets/automations-execution.webp";
import type { FC } from "react";

type AutomationsListEmptyProps = {
    onTemplateSelect: (template: AutomationTemplate) => void;
};

export const AutomationsListEmpty: FC<AutomationsListEmptyProps> = ({ onTemplateSelect }) => {
    return (
        <div className="rounded-xl border border-border-subtle" data-testid="workflows-empty-state">
            <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6 md:flex-row">
                <div className="order-2 flex shrink grow flex-col gap-4 md:order-1 md:min-w-[360px] md:max-w-[360px]">
                    <div className="flex flex-col gap-1">
                        <Heading2>Get started with a Automation template</Heading2>
                        <Text className="text-base font-normal text-content-muted">
                            Build automations tailored to your team&apos;s needs, from simple automations to large-scale
                            reviews and migrations.
                        </Text>
                    </div>
                    {Object.values(TEMPLATE_GROUPS).map((group, groupIndex) => (
                        <div key={group.id} className="flex flex-col gap-2">
                            {group.label && groupIndex > 0 && (
                                <Text className="mb-1 text-xs font-medium text-content-muted">{group.label}</Text>
                            )}
                            <div className="flex flex-col gap-4">
                                {/* the type annotation is required otherwise template is any for some reason */}
                                {Object.values(group.templates).map((template: AutomationTemplate) => (
                                    <AutomationTemplateCard
                                        template={template}
                                        onSelect={onTemplateSelect}
                                        key={template.id}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="order-1 min-w-0 shrink md:order-3">
                    <img
                        className="rounded-lg border border-border-subtle shadow-sm"
                        src={workflowDashboardImage}
                        alt="Ona Automations dashboard"
                    />
                </div>
            </div>
        </div>
    );
};

const AutomationTemplateCard: FC<{
    template: AutomationTemplate;
    onSelect: (template: AutomationTemplate) => void;
}> = ({ template, onSelect }) => {
    return (
        <button
            className={cn(
                "flex items-center gap-3 rounded-xl border border-border-base bg-surface-popover p-4 transition-[border-color,box-shadow]",
                template.enabled
                    ? "hover:border-border-strong hover:shadow-sm"
                    : "opacity-50 disabled:bg-surface-popover",
            )}
            onClick={() => onSelect(template)}
            data-tracking-id={`automation-template-card-select-${template.id}`}
            disabled={!template.enabled}
        >
            <div className={cn("rounded-[4px] p-1", template.iconBgColor, template.iconColor)}>
                <template.icon size={16} />
            </div>
            <Text className="grow text-left text-base font-medium">{template.title}</Text>
            <IconChevronRight size={16} className="shrink-0 text-content-primary" />
        </button>
    );
};
